import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

const DEDUP_TTL_MS = 5 * 60 * 1000;
const RATE_LIMIT_WINDOW_MS = 60 * 1000;
const RATE_LIMIT_MAX = 10;
const CIRCUIT_WINDOW_MS = 60 * 1000;
const CIRCUIT_THRESHOLD = 30;
const CIRCUIT_OPEN_MS = 10 * 60 * 1000;
const STACK_MAX_CHARS = 2000;

interface ReportContext {
  error: unknown;
  source: 'errorHandler' | 'manual';
}

@Injectable({ providedIn: 'root' })
export class SlackErrorReporterService {
  private readonly fingerprints = new Map<string, number>();
  private readonly sentTimestamps: number[] = [];
  private readonly receivedTimestamps: number[] = [];

  private circuitOpenUntil = 0;
  private suppressedDuringOpenCircuit = 0;
  private isSending = false;

  report(context: ReportContext): void {
    if (this.isSending) {
      return;
    }
    if (!environment.errorReporting?.enabled) {
      return;
    }
    if (!environment.errorReporting.slackWebhookUrl) {
      return;
    }
    // Se o front está rodando local (localhost / 127.0.0.1 / ::1 / *.local),
    // não polui o canal do Slack — o desenvolvedor vê os erros no próprio
    // devtools. Serve de salvaguarda caso alguém rode `ng serve` com uma
    // configuration que aponte pra um webhook real.
    if (this.isLocalHost()) {
      return;
    }

    const now = Date.now();
    this.trackReceived(now);

    if (this.isCircuitOpen(now)) {
      this.suppressedDuringOpenCircuit += 1;
      return;
    }

    if (this.tripCircuitIfNeeded(now)) {
      return;
    }

    const fingerprint = this.fingerprintOf(context.error);
    if (this.isDuplicate(fingerprint, now)) {
      return;
    }

    if (!this.allowByRateLimit(now)) {
      return;
    }

    this.fingerprints.set(fingerprint, now);
    this.sentTimestamps.push(now);
    void this.send(this.buildPayload(context.error));
  }

  private trackReceived(now: number): void {
    this.receivedTimestamps.push(now);
    const cutoff = now - CIRCUIT_WINDOW_MS;
    while (this.receivedTimestamps.length && this.receivedTimestamps[0] < cutoff) {
      this.receivedTimestamps.shift();
    }
  }

  private isCircuitOpen(now: number): boolean {
    if (this.circuitOpenUntil === 0) {
      return false;
    }
    if (now < this.circuitOpenUntil) {
      return true;
    }
    const suppressed = this.suppressedDuringOpenCircuit;
    this.circuitOpenUntil = 0;
    this.suppressedDuringOpenCircuit = 0;
    if (suppressed > 0) {
      void this.send(this.buildCircuitClosePayload(suppressed));
      this.sentTimestamps.push(now);
    }
    return false;
  }

  private tripCircuitIfNeeded(now: number): boolean {
    if (this.receivedTimestamps.length < CIRCUIT_THRESHOLD) {
      return false;
    }
    this.circuitOpenUntil = now + CIRCUIT_OPEN_MS;
    this.suppressedDuringOpenCircuit = 0;
    void this.send(this.buildCircuitOpenPayload(this.receivedTimestamps.length, this.circuitOpenUntil));
    this.sentTimestamps.push(now);
    return true;
  }

  private isDuplicate(fingerprint: string, now: number): boolean {
    const cutoff = now - DEDUP_TTL_MS;
    for (const [key, ts] of this.fingerprints) {
      if (ts < cutoff) {
        this.fingerprints.delete(key);
      }
    }
    const seen = this.fingerprints.get(fingerprint);
    return seen !== undefined && seen >= cutoff;
  }

  private allowByRateLimit(now: number): boolean {
    const cutoff = now - RATE_LIMIT_WINDOW_MS;
    while (this.sentTimestamps.length && this.sentTimestamps[0] < cutoff) {
      this.sentTimestamps.shift();
    }
    return this.sentTimestamps.length < RATE_LIMIT_MAX;
  }

  private fingerprintOf(error: unknown): string {
    const message = this.messageOf(error);
    const stack = this.stackOf(error);
    const firstStackLine = stack.split('\n').find(line => line.trim().length > 0) ?? '';
    return `${message}::${firstStackLine}`;
  }

  private messageOf(error: unknown): string {
    if (error instanceof Error) {
      return error.message || error.name || 'Error';
    }
    if (typeof error === 'string') {
      return error;
    }
    try {
      return JSON.stringify(error) ?? 'unknown error';
    } catch {
      return 'unknown error';
    }
  }

  private stackOf(error: unknown): string {
    if (error instanceof Error && error.stack) {
      return error.stack.length > STACK_MAX_CHARS
        ? `${error.stack.slice(0, STACK_MAX_CHARS)}…`
        : error.stack;
    }
    return '';
  }

  private buildPayload(error: unknown): unknown {
    const message = this.messageOf(error);
    const stack = this.stackOf(error);
    const user = this.readUserInfo();
    const lines: string[] = [
      `*[${environment.errorReporting.environmentTag}]* erro no frontend`,
      `*Mensagem:* ${message}`,
      `*URL:* ${this.safeLocation()}`,
      `*User:* ${user ?? 'anônimo'}`,
      `*UA:* ${this.safeUserAgent()}`
    ];
    if (stack) {
      lines.push('*Stack:*', '```', stack, '```');
    }
    return { text: lines.join('\n') };
  }

  private buildCircuitOpenPayload(burstCount: number, openUntil: number): unknown {
    const until = new Date(openUntil).toISOString();
    return {
      text: [
        `*[${environment.errorReporting.environmentTag}]* :rotating_light: circuit breaker aberto`,
        `Recebidos ${burstCount} erros em ${Math.round(CIRCUIT_WINDOW_MS / 1000)}s — possível loop.`,
        `Novos erros serão suprimidos até ${until}.`
      ].join('\n')
    };
  }

  private buildCircuitClosePayload(suppressed: number): unknown {
    return {
      text: [
        `*[${environment.errorReporting.environmentTag}]* circuit breaker fechado.`,
        `${suppressed} erro(s) suprimido(s) durante a janela.`
      ].join('\n')
    };
  }

  private async send(payload: unknown): Promise<void> {
    this.isSending = true;
    try {
      // Slack webhooks não retornam CORS, então o request precisa ser "simple":
      // Content-Type safelisted (application/x-www-form-urlencoded) + body
      // no formato legado `payload=<json>` aceito pelo Slack.
      const body = 'payload=' + encodeURIComponent(JSON.stringify(payload));
      await fetch(environment.errorReporting.slackWebhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
        keepalive: true
      });
    } catch {
      // engolido de propósito: reportar uma falha de report causaria loop
    } finally {
      this.isSending = false;
    }
  }

  private safeLocation(): string {
    try {
      return typeof window !== 'undefined' ? window.location.href : 'n/a';
    } catch {
      return 'n/a';
    }
  }

  private isLocalHost(): boolean {
    try {
      if (typeof window === 'undefined') return false;
      const host = (window.location.hostname || '').toLowerCase();
      return (
        host === 'localhost' ||
        host === '127.0.0.1' ||
        host === '::1' ||
        host === '0.0.0.0' ||
        host.endsWith('.local') ||
        host.endsWith('.localhost')
      );
    } catch {
      return false;
    }
  }

  private safeUserAgent(): string {
    try {
      return typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a';
    } catch {
      return 'n/a';
    }
  }

  private readUserInfo(): string | null {
    try {
      if (typeof localStorage === 'undefined') {
        return null;
      }
      const raw = localStorage.getItem('user_data');
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const id = parsed['id'] ?? parsed['userId'];
      const email = parsed['email'] ?? parsed['userName'] ?? parsed['login'] ?? parsed['username'];
      if (id && email) {
        return `${String(email)} (${String(id)})`;
      }
      return (email ?? id) ? String(email ?? id) : null;
    } catch {
      return null;
    }
  }
}
